import { T, useGT, Var } from 'gt-react';
import {
  SenderRouteKindSchema,
  type MemoryRuleAddress,
} from '@erp/shared-types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  ProposeMemoryDocumentScopeSchema,
  senderDomainOf,
  type ProposeMemoryDocumentScope,
  type ProposeMemoryRoutingFields,
} from './proposeMemoryDraft';

/** Field groups for the propose-memory form, one per memory kind. */

export function NoteField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useGT();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="memory-note">
        <T context="label: free-text memory note">Note</T>
      </Label>
      <Textarea
        id="memory-note"
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t(
          'e.g. Coreptec writes “Maritimo” when they mean ocean freight',
          { context: 'placeholder for a memory note' },
        )}
      />
    </div>
  );
}

export function CustomerField({
  required,
  customerId,
  onChange,
  limitsHint = false,
}: {
  required: boolean;
  customerId: string | null;
  onChange: (customerId: string | null) => void;
  /** The pick limits an extraction hint to this customer's documents. */
  limitsHint?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>
        {required ? (
          <T context="label: the customer an alias binds to">Customer</T>
        ) : (
          <T context="label: optional customer scope for a memory">
            Customer (optional)
          </T>
        )}
      </Label>
      <CustomersAutocomplete
        selectedCustomerId={customerId ?? undefined}
        onCustomerSelect={(id) => onChange(id ?? null)}
        allowClear
      />
      {limitsHint ? (
        <p className="text-xs text-muted-foreground">
          <T context="help text: an extraction hint limited to one customer">
            When set, the hint applies only to this customer&apos;s documents,
            and only once the customer is known before extraction.
          </T>
        </p>
      ) : null}
    </div>
  );
}

export function AliasField({
  alias,
  onChange,
  bindsTo,
}: {
  alias: string;
  onChange: (alias: string) => void;
  /** Customer name (or id) the alias will bind to, when known. */
  bindsTo: string | null;
}) {
  const t = useGT();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor="memory-alias">
        <T context="label: the extracted name that should map to the customer">
          Name as it appears on documents
        </T>
      </Label>
      <Input
        id="memory-alias"
        value={alias}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('e.g. SORIM HOLDING LTD', {
          context: 'placeholder for a customer alias',
        })}
      />
      {bindsTo ? (
        <p className="text-xs text-muted-foreground">
          <T context="which customer the alias will bind to">
            Will bind to <Var>{bindsTo}</Var>.
          </T>
        </p>
      ) : null}
    </div>
  );
}

export function CarrierTermFields({
  term,
  code,
  onTermChange,
  onCodeChange,
}: {
  term: string;
  code: string;
  onTermChange: (term: string) => void;
  onCodeChange: (code: string) => void;
}) {
  const t = useGT();
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="flex flex-col gap-1">
        <Label htmlFor="memory-term">
          <T context="label: phrase in the document">Phrase</T>
        </Label>
        <Input
          id="memory-term"
          value={term}
          onChange={(e) => onTermChange(e.target.value)}
          placeholder={t('e.g. Maritimo', {
            context: 'placeholder for a carrier phrase',
          })}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="memory-code">
          <T context="label: shipping method code">Shipping method code</T>
        </Label>
        <Input
          id="memory-code"
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          placeholder="OCEAN"
        />
      </div>
    </div>
  );
}

function addressLine(address: MemoryRuleAddress): string {
  return [address.street, address.city, address.postalCode, address.country]
    .filter(Boolean)
    .join(', ');
}

export function OwnSiteFields({
  label,
  onLabelChange,
  address,
  onAddressChange,
  /** When false the address is the draft's ship-to and is shown read-only. */
  editableAddress,
}: {
  label: string;
  onLabelChange: (label: string) => void;
  address: MemoryRuleAddress;
  onAddressChange: (address: MemoryRuleAddress) => void;
  editableAddress: boolean;
}) {
  const t = useGT();
  // Built outside the JSX so the i18n literal-string lint sees only
  // translated text; the keys are data, not copy.
  const addressLabels: Array<[keyof MemoryRuleAddress, string]> = [
    ['street', t('Street', { context: 'address field' })],
    ['city', t('City', { context: 'address field' })],
    ['postalCode', t('Postal code', { context: 'address field' })],
    ['country', t('Country', { context: 'address field' })],
  ];
  const addressInputs = addressLabels.map(([key, text]) => (
    <Input
      key={key}
      aria-label={text}
      placeholder={text}
      value={address[key] ?? ''}
      onChange={(e) =>
        onAddressChange({ ...address, [key]: e.target.value || null })
      }
    />
  ));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Label htmlFor="memory-label">
          <T context="label: name for one of our own sites">Site name</T>
        </Label>
        <Input
          id="memory-label"
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder={t('e.g. De Meern warehouse', {
            context: 'placeholder for an own-site label',
          })}
        />
      </div>
      {editableAddress ? (
        <div className="grid grid-cols-2 gap-2">{addressInputs}</div>
      ) : (
        <p className="text-xs text-muted-foreground">{addressLine(address)}</p>
      )}
    </div>
  );
}

const NONE = '__none__';

/** A partial update of the routing fields, merged by the form. */
type RoutingChange = (patch: Partial<ProposeMemoryRoutingFields>) => void;

/** Which documents a sender route or extraction hint applies to. */
export function DocumentScopeField({
  value,
  onChange,
}: {
  value: ProposeMemoryDocumentScope;
  onChange: (value: ProposeMemoryDocumentScope) => void;
}) {
  const t = useGT();
  return (
    <div className="flex flex-col gap-1">
      <Label>
        <T context="label: which document types a memory applies to">
          Applies to
        </T>
      </Label>
      <Select
        value={value}
        onValueChange={(v) =>
          onChange(ProposeMemoryDocumentScopeSchema.parse(v))
        }
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            {t('Every document', { context: 'document scope option' })}
          </SelectItem>
          <SelectItem value="purchase_order">
            {t('Purchase orders', { context: 'document scope option' })}
          </SelectItem>
          <SelectItem value="discount_request">
            {t('Discount requests', { context: 'document scope option' })}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Sender → organization route: the sender (whole address or its domain),
 * the sibling organization its documents belong to, and an optional note
 * the organization classifier reads. The current org is never a target —
 * routes are stored in the org that classifies.
 */
export function SenderRouteFields({
  routing,
  onRoutingChange,
  note,
  onNoteChange,
  targets,
}: {
  routing: ProposeMemoryRoutingFields;
  onRoutingChange: RoutingChange;
  note: string;
  onNoteChange: (note: string) => void;
  /** The sibling organizations a sender can route to (never the current one). */
  targets: ReadonlyArray<{ id: string; name: string }>;
}) {
  const t = useGT();
  const domain = senderDomainOf(routing.sender);
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="memory-sender">
            <T context="label: the email sender a routing memory is about">
              Sender
            </T>
          </Label>
          <Input
            id="memory-sender"
            value={routing.sender}
            onChange={(e) => onRoutingChange({ sender: e.target.value })}
            placeholder={t('e.g. jaanus@markline.ee', {
              context: 'placeholder for a sender address',
            })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label>
            <T context="label: match the whole address or its domain">Match</T>
          </Label>
          <Select
            value={routing.senderKind}
            onValueChange={(v) =>
              onRoutingChange({ senderKind: SenderRouteKindSchema.parse(v) })
            }
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email_domain">
                {t('The domain', { context: 'sender match option' })}
              </SelectItem>
              <SelectItem value="email_address">
                {t('This address only', { context: 'sender match option' })}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {routing.senderKind === 'email_domain' && domain ? (
        <p className="text-xs text-muted-foreground">
          <T context="which domain the sender route will match">
            Will match every sender at <Var>{domain}</Var>.
          </T>
        </p>
      ) : null}
      <div className="flex flex-col gap-1">
        <Label>
          <T context="label: the organization a sender routes to">
            Route to organization
          </T>
        </Label>
        <Select
          value={routing.targetOrganizationId ?? NONE}
          onValueChange={(v) =>
            onRoutingChange({ targetOrganizationId: v === NONE ? null : v })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>
              {t('No fixed route — note only', {
                context: 'sender route target option',
              })}
            </SelectItem>
            {targets.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DocumentScopeField
        value={routing.documentScope}
        onChange={(documentScope) => onRoutingChange({ documentScope })}
      />
      <div className="flex flex-col gap-1">
        <Label htmlFor="memory-route-note">
          <T context="label: optional note for the organization classifier">
            Note for the classifier (optional)
          </T>
        </Label>
        <Textarea
          id="memory-route-note"
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder={t(
            'e.g. Baltic distributor served by the EMEA entity; their forms never name us',
            { context: 'placeholder for a sender routing note' },
          )}
        />
      </div>
    </div>
  );
}

/**
 * Extraction hint: free text appended to one field of the extraction schema
 * (`*` for the whole document), optionally only for one sender domain.
 */
export function ExtractionHintFields({
  routing,
  onRoutingChange,
  hint,
  onHintChange,
}: {
  routing: ProposeMemoryRoutingFields;
  onRoutingChange: RoutingChange;
  hint: string;
  onHintChange: (hint: string) => void;
}) {
  const t = useGT();
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="memory-field-path">
            <T context="label: extraction schema field the hint applies to">
              Field
            </T>
          </Label>
          <Input
            id="memory-field-path"
            value={routing.fieldPath}
            onChange={(e) => onRoutingChange({ fieldPath: e.target.value })}
            placeholder={t('e.g. line_items.sku, or * for the whole document', {
              context: 'placeholder for an extraction hint field path',
            })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="memory-hint-domain">
            <T context="label: optional sender domain scope for a hint">
              Sender domain (optional)
            </T>
          </Label>
          <Input
            id="memory-hint-domain"
            value={routing.senderDomain}
            onChange={(e) => onRoutingChange({ senderDomain: e.target.value })}
            placeholder={t('e.g. markline.ee', {
              context: 'placeholder for a sender domain',
            })}
          />
        </div>
      </div>
      <DocumentScopeField
        value={routing.documentScope}
        onChange={(documentScope) => onRoutingChange({ documentScope })}
      />
      <div className="flex flex-col gap-1">
        <Label htmlFor="memory-hint">
          <T context="label: the extraction hint text">Hint</T>
        </Label>
        <Textarea
          id="memory-hint"
          rows={3}
          maxLength={300}
          value={hint}
          onChange={(e) => onHintChange(e.target.value)}
          placeholder={t(
            'e.g. The distributor is in the “Company” cell at the top, not the end customer',
            { context: 'placeholder for an extraction hint' },
          )}
        />
      </div>
    </div>
  );
}
